<template>
  <div>
    <div class="p-5 flex flex-col">
      <span class="font-semibold text-3xl">List Permission</span>
      <div class="mt-5 card-group">
        <span class="mb-3 font-semibold">Group</span>
        <div class="w-1/2">
          <select-with-search
            is-object-model
            v-model="group"
            placeholder="Select group"
            :options="groupOptions"
          />
        </div>
      </div>
      <div
        v-show="isLoading"
        class="px-1"
      >
        <vs-progress
          indeterminate
          color="secondary"
        />
      </div>
      <div class="mt-10 flex flex-col">
        <span class="mb-5 font-semibold">Access</span>
        <div
          v-for="(item, index) of permissionData"
          :key="index"
        >
          <div class="access-card">
            <div class="w-1 h-8 rounded-lg bg-secondary" />
            <div class="ml-5 mr-2">
              <transition name="spin">
                <div
                  v-show="isUserCanAccessMenu(pagePermission.name, 'edit')"
                  class="checkbox"
                  :key="item.isChecked"
                  :class="[item.isChecked
                             ? 'checkbox-active'
                             : 'checkbox-inactive',
                           {'checkbox-disabled': !isGroupCanUpdate}
                  ]"
                  @click="handleParentChecked(item)"
                >
                  <vs-icon
                    icon="check"
                    color="#FFFFFF"
                    size="8px"
                  />
                </div>
              </transition>
            </div>
            <span class="font-semibold text-secondary">{{ startCase(item.name) }}</span>
            <div class="ml-auto mr-56 flex flex-row">
              <div
                v-for="(permission, pIndex) of item.permissions"
                class="mr-10 flex flex-row items-center"
                :key="pIndex"
              >
                <div class="mr-2">
                  <transition name="spin">
                    <div
                      v-show="isUserCanAccessMenu(pagePermission.name, 'edit')"
                      class="checkbox"
                      :key="permission.isChecked"
                      :class="[permission.isChecked
                                 ? 'checkbox-active'
                                 : 'checkbox-inactive',
                               {'checkbox-disabled': !isGroupCanUpdate}
                      ]"
                      @click="handleParentPermissionChecked(item, item.permissions, permission)"
                    >
                      <vs-icon
                        icon="check"
                        color="#FFFFFF"
                        size="8px"
                      />
                    </div>
                  </transition>
                </div>
                <span>{{ startCase(permission.name) }}</span>
              </div>
            </div>
          </div>
          <div
            v-for="(child, cIndex) of item.children"
            :key="cIndex"
          >
            <div class="ml-12 access-card access-card-child">
              <span>{{ startCase(child.name) }}</span>
              <div class="ml-auto mr-56 flex flex-row">
                <div
                  v-for="(permission, pIndex) of child.permissions"
                  class="mr-10 flex flex-row items-center"
                  :key="`p-${pIndex}`"
                >
                  <div class="mr-2">
                    <transition name="spin">
                      <div
                        v-show="isUserCanAccessMenu(pagePermission.name, 'edit')"
                        class="checkbox"
                        :id="`${child.id}-${pIndex}`"
                        :key="permission.isChecked"
                        :class="[permission.isChecked
                                   ? 'checkbox-active'
                                   : 'checkbox-inactive',
                                 {'checkbox-disabled': !isGroupCanUpdate}
                        ]"
                        @click="handleChildrenPermissionChecked(item, child, permission)"
                      >
                        <vs-icon
                          icon="check"
                          color="#FFFFFF"
                          size="8px"
                        />
                      </div>
                    </transition>
                  </div>
                  <span>{{ startCase(permission.name) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-row justify-end">
        <vs-button
          ref="submitButton"
          v-show="group && isUserCanAccessMenu(pagePermission.name, 'edit')"
          type="filled"
          color="secondary"
          class="w-40 font-semibold vs-con-loading__container"
          :disabled="isLoading || isProcessing"
          @click="updatePermission"
        >
          Save
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import SelectWithSearch from '@/components/selectWithSearch'

export default {
  components: {
    SelectWithSearch
  },
  data() {
    return {
      isLoading: false,
      isProcessing: false,
      isGroupCanUpdate: true,
      group: null,
      groupOptions: [],
      permissionData: [],
      permissionChecklist: [
        {
          isChecked: false,
          name: 'view'
        },
        {
          isChecked: false,
          name: 'add'
        },
        {
          isChecked: false,
          name: 'delete'
        },
        {
          isChecked: false,
          name: 'edit'
        },
        {
          isChecked: false,
          name: 'is_active'
        },
        {
          isChecked: false,
          name: 'acknowledge'
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      groups: 'Group/groups',
      permissions: 'Permission/permissions'
    }),
    pagePermission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    }
  },
  watch: {
    groups: {
      handler(val) {
        this.groupOptions = val.map(item => ({ value: item.id, text: item.name }))
      }
    },
    group: {
      handler(val) {
        if (val) {
          this.fetchPermissionsByGroup({ id: val.value })
          return
        }

        this.permissionData = []
      }
    },
    permissions: {
      handler(val) {
        this.isGroupCanUpdate = val.can_update
        this.permissionData = this.transformPermissionData(val.data)
      }
    }
  },
  mounted() {
    this.fetchGroups()
  },
  methods: {
    async fetchGroups() {
      try {
        await this.$store.dispatch('Group/fetchGroups', { isActiveAll: true })
      } catch (err) {
        this.showFailedNotification(this, 'Failed to fetch group, please try again')
      }
    },
    async fetchPermissionsByGroup(form) {
      try {
        this.isLoading = true

        await this.$store.dispatch('Permission/fetchPermissions', form)

        this.isLoading = false
      } catch (err) {
        this.isLoading = false
        this.showFailedNotification(this, 'Failed to fetch permissions, please try again')
      }
    },
    async updatePermission() {
      try {
        this.isProcessing = true
        this.changeButtonState(this.$refs.submitButton.$el)

        await this.$store.dispatch('Permission/putPermissions',
          { id: this.group.value, data: this.transformForm() }
        )

        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        this.showSuccessNotification(this, 'You have successfully update permission')

        await this.fetchPermissionsByGroup({ id: this.group.value })
      } catch (err) {
        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        this.showFailedNotification(this, 'Failed to update permissions, please try again')
      }
    },
    handleParentChecked(item) {
      if (!this.isGroupCanUpdate) {
        return
      }

      item.isChecked = !item.isChecked
      if (item.children.length) {
        item.children.forEach(child => {
          child.permissions.forEach(permission => {
            permission.isChecked = item.isChecked
          })
        })
        return
      }

      item.permissions.forEach(permission => {
        permission.isChecked = item.isChecked
      })
    },
    handleParentPermissionChecked(parent, data, child) {
      if (!this.isGroupCanUpdate) {
        return
      }

      child.isChecked = !child.isChecked
      parent.isChecked = !data.some(permission => !permission.isChecked)
    },
    handleChildrenPermissionChecked(parent, child, permission) {
      if (!this.isGroupCanUpdate) {
        return
      }

      permission.isChecked = !permission.isChecked
      parent.isChecked = !parent.children.some(child => child.permissions.some(cPermission => !cPermission.isChecked))
    },
    transformPermissionData(data) {
      return data.map(item => {
        item.permissions = item.children.length
          ? item.permissions
          : this.transformChecklistPermission(item.id, item.permissions)

        if (item.children.length) {
          item.children.forEach(child => {
            child.permissions = this.transformChecklistPermission(child.id, child.permissions)
          })
        }

        const isParentPermissionFull = item.permissions.length
          ? !item.permissions.some(permission => !permission.isChecked)
          : true

        const isChildrenPermissionFull = item.children.length
          ? !item.children.some(child => child.permissions.some(permission => !permission.isChecked))
          : true

        return {
          ...item,
          isChecked: item.permissions.length
            ? isParentPermissionFull
            : isChildrenPermissionFull
        }
      })
    },
    transformChecklistPermission(id, data) {
      return this.permissionChecklist.map((checklist, index) => {
        const existPermission = data.length
          ? data.find(item => item.toLowerCase() === checklist.name.toLowerCase())
          : false

        return {
          /** `id` added for reactivity inside double nested elements
           * if `id` not added, when we checked `permission`
           * every `permission` with same `name` will be automatically checked (not expected behaviour)
          **/
          id: `${id}-${index}`,
          isChecked: !!existPermission,
          name: checklist.name
        }
      })
    },
    transformForm() {
      const clonePermissionData = this.cloneDeep(this.permissionData)
      clonePermissionData.forEach(item => {
        delete item.isChecked
        item.permissions = item.permissions
          .filter(permission => permission.isChecked)
          .map(permission => permission.name)
      })

      clonePermissionData.forEach(item => {
        if (item.children.length) {
          item.children.forEach(child => {
            child.permissions = child.permissions
              .filter(permission => permission.isChecked)
              .map(permission => permission.name)
          })
        }
      })

      return clonePermissionData
    },
    changeButtonState(container) {
      if (this.isProcessing) {
        this.$vs.loading({
          background: 'secondary',
          color: '#FFF',
          container,
          scale: 0.35
        })
        return
      }

      this.$vs.loading.close(container)
    }
  }
}
</script>

<style lang="scss" scoped>
.card-group {
  @apply p-5 flex flex-col bg-white rounded-lg;
}

.card-item {
  @apply mx-2 py-2 px-3 flex flex-row items-center rounded-lg cursor-pointer;

  background-color: rgba(255, 127, 77, 0.15);
  &-active {
    @apply bg-secondary;
  }
}

.access-card {
  @apply px-2 py-3 mb-5 flex flex-row items-center bg-white rounded-lg;

  &-child {
    @apply pl-5;
  }
}

.checkbox {
  @apply p-1 flex flex-row border border-secondary rounded-md cursor-pointer;

  &-active {
    @apply bg-secondary;
  }

  &-inactive {
    @apply bg-white;
  }

  &-disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}

.spin-enter-active {
  animation: spin 0.5s;
}

.spin-leave-active {
  display: none;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  50% {
    transform: rotate(90deg);
  }

  100% {
    transform: rotate(0deg);
  }
}
</style>
